import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  ArrowLeft,
  Camera,
  CheckCircle,
  Clock,
  CreditCard,
  IndianRupee,
  MapPin,
  Navigation,
  Phone,
  PlayCircle,
  QrCode,
  Radio,
  RefreshCw,
  Train,
  Upload,
  Wrench,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { ChecklistPayload, LiveRepairSession, Order, PaymentQrResponse } from '../types';

type ChecklistState = Omit<ChecklistPayload, 'orderId' | 'checkedBy'>;

const defaultChecklist: ChecklistState = {
  cameraWorking: true,
  micWorking: true,
  powerButtonWorking: true,
  batteryWorking: true,
  chargingWorking: true,
  displayWorking: true,
  callingWorking: true,
};

const checklistItems: Array<{ key: keyof ChecklistState; label: string }> = [
  { key: 'cameraWorking', label: 'Camera' },
  { key: 'micWorking', label: 'Mic' },
  { key: 'powerButtonWorking', label: 'Power Button' },
  { key: 'batteryWorking', label: 'Battery' },
  { key: 'chargingWorking', label: 'Charging' },
  { key: 'displayWorking', label: 'Display' },
  { key: 'callingWorking', label: 'Calling' },
];

const formatDate = (date?: string) => date ? new Date(date).toLocaleDateString('en-IN') : '-';
const formatAmount = (amount?: number) => `Rs ${Number(amount || 0).toLocaleString('en-IN')}`;

function parseScheduledDt(scheduledDate: string, scheduledTime: string): Date | null {
  try {
    const datePart = scheduledDate.split('T')[0];
    const [y, mo, d] = datePart.split('-').map(Number);
    const match = scheduledTime.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
    if (!match) return null;
    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const period = match[3]?.toUpperCase();
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return new Date(y, mo - 1, d, h, m, 0, 0);
  } catch { return null; }
}

const normalizeStatus = (status?: string) => (status || '').trim().toLowerCase();

const getErrorMessage = (error: unknown, fallback: string) => {
  const apiError = error as {
    response?: { data?: { message?: string; error?: string; errors?: string[] } | string };
    message?: string;
  };
  const data = apiError.response?.data;

  if (typeof data === 'string') {
    return data;
  }
  if (data?.errors?.length) {
    return data.errors.join(', ');
  }
  return data?.message || data?.error || apiError.message || fallback;
};

const OrderDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const beforePhotoRef = useRef<HTMLInputElement>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistState>(defaultChecklist);
  const [afterPhoto, setAfterPhoto] = useState<File | null>(null);
  const [paymentQr, setPaymentQr] = useState<PaymentQrResponse | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [liveSession, setLiveSession] = useState<LiveRepairSession | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);

  const technicianId = user?.technicianIds?.[0] || user?.id || 0;

  const loadOrder = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError('');
    try {
      const response = await apiService.getOrder(Number(id));
      setOrder(response.entity);
    } catch {
      setError('Unable to load order details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  // Tick every 30s to update the schedule countdown
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(t);
  }, []);

  // Load existing live session when order loads
  useEffect(() => {
    if (!order) return;
    apiService.getLiveSessionByOrder(order.id)
      .then(res => { if (res?.entity?.isActive) setLiveSession(res.entity); })
      .catch(() => {});
  }, [order]);

  const orderStatus = normalizeStatus(order?.orderStatus);
  const paymentStatus = normalizeStatus(order?.paymentStatus);
  const isPending = orderStatus === 'pending' || orderStatus === 'assigned';
  const isInProgress = orderStatus === 'service inprogress' || orderStatus === 'service in progress';
  const isServiceDone = orderStatus === 'service done' || orderStatus === 'completed';
  const isPaymentReceived =
    orderStatus === 'payment received' ||
    paymentStatus === 'paid' ||
    paymentStatus === 'success' ||
    paymentStatus === 'payment received';
  const shouldShowPaymentQr = !!order && isServiceDone && !isPaymentReceived;
  const qrImageSrc = paymentQr?.qrCodeBase64
    ? paymentQr.qrCodeBase64.startsWith('data:')
      ? paymentQr.qrCodeBase64
      : `data:image/png;base64,${paymentQr.qrCodeBase64}`
    : '';

  useEffect(() => {
    if (!order || !shouldShowPaymentQr) {
      setPaymentQr(null);
      return;
    }

    let cancelled = false;

    const loadPaymentQr = async () => {
      setPaymentLoading(true);
      try {
        const qr = await apiService.getPaymentQr(order.id);
        if (!cancelled) {
          setPaymentQr(qr);
        }
      } catch (err) {
        if (!cancelled) {
          setActionError(getErrorMessage(err, 'Unable to load payment QR'));
        }
      } finally {
        if (!cancelled) {
          setPaymentLoading(false);
        }
      }
    };

    loadPaymentQr();

    const timer = window.setInterval(async () => {
      try {
        const status = await apiService.getPaymentStatus(order.id);
        const normalized = status.toUpperCase();
        if (normalized.includes('PAID') || normalized.includes('SUCCESS')) {
          window.clearInterval(timer);
          setPaymentConfirmed(true);
          setActionMessage('Payment received. Order refreshed.');
          await loadOrder();
        }
      } catch {
        // Polling should not block the technician workflow.
      }
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [loadOrder, order, shouldShowPaymentQr]);

  const resetActionState = () => {
    setActionMessage('');
    setActionError('');
    setPaymentConfirmed(false);
  };

  const handleBeforePhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !order) return;

    resetActionState();
    setActionLoading(true);
    try {
      // Update status immediately (fast) so technician isn't blocked
      await apiService.updateOrderStatus(order.id, 'Service InProgress', technicianId);
      setActionMessage('Order started. Photo uploading in background...');
      await loadOrder();
      // Fire-and-forget the photo upload
      apiService.uploadBeforePhoto(order.id, file, technicianId).catch(() => {/* silent */});
    } catch (err) {
      setActionError(getErrorMessage(err, 'Unable to start order'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleChecklistChange = (key: keyof ChecklistState) => {
    setChecklist((current) => ({ ...current, [key]: !current[key] }));
  };

  const handleCompleteOrder = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!order) return;

    resetActionState();
    if (!afterPhoto) {
      setActionError('Please upload the after repair photo.');
      return;
    }

    setActionLoading(true);
    const photoToUpload = afterPhoto;
    try {
      await apiService.saveChecklist(order.id, {
        orderId: order.id,
        ...checklist,
        checkedBy: technicianId || undefined,
      });
      // Update status immediately (fast) so technician isn't blocked
      await apiService.updateOrderStatus(order.id, 'Service Done', technicianId);
      setChecklistOpen(false);
      setAfterPhoto(null);
      setActionMessage('Order completed. After photo uploading in background...');
      await loadOrder();
      // Fire-and-forget the photo upload
      apiService.uploadAfterPhoto(order.id, photoToUpload, technicianId).catch(() => {/* silent */});
    } catch (err) {
      setActionError(getErrorMessage(err, 'Unable to complete order'));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePaymentReceived = async (method: string) => {
    if (!order) return;

    resetActionState();
    setActionLoading(true);
    try {
      await apiService.updateOrderStatus(order.id, 'Payment Received', technicianId, method);
      setActionMessage(`Payment marked received via ${method}.`);
      await loadOrder();
    } catch (err) {
      setActionError(getErrorMessage(err, 'Unable to mark payment received'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleGoLive = async () => {
    if (!order) return;
    setLiveLoading(true);
    try {
      const res = await apiService.startLiveSession({
        orderId: order.id, orderNumber: order.orderNumber,
        technicianId: technicianId, technicianName: order.technicianName || undefined,
        makeName: order.makeName, modelName: order.modelName,
        serviceSummary: order.orderItems?.map(i => i.serviceName).join(', '),
      });
      if (res?.entity) { setLiveSession(res.entity); }
    } catch { /* silent */ } finally { setLiveLoading(false); }
  };

  const handleEndLive = async () => {
    if (!liveSession) return;
    setLiveLoading(true);
    try {
      await apiService.endLiveSession(liveSession.id);
      setLiveSession(null);
    } catch { /* silent */ } finally { setLiveLoading(false); }
  };

  if (loading) {
    return <div className="empty-state mobile-empty">Loading order...</div>;
  }

  if (error || !order) {
    return (
      <div>
        <button className="back-button" onClick={() => navigate('/orders')}>
          <ArrowLeft size={16} />
          Orders
        </button>
        <div className="empty-state mobile-empty">{error || 'Order not found'}</div>
      </div>
    );
  }

  return (
    <div>
      <button className="back-button" onClick={() => navigate('/orders')}>
        <ArrowLeft size={16} />
        Orders
      </button>

      <div className="detail-hero-card">
        <div className="detail-hero-top">
          <div>
            <span>Order</span>
            <strong>{order.orderNumber}</strong>
          </div>
          <span className="status-pill">{order.orderStatus}</span>
        </div>
        <div className="detail-device">{order.makeName} {order.modelName}</div>
        <div className="single-order-money-grid">
          <div className="money-tile service">
            <Wrench size={15} />
            <span>You Earn</span>
            <strong style={{ fontSize: 20 }}>{formatAmount(order.totalServiceCost)}</strong>
          </div>
          <div className="money-tile amount">
            <IndianRupee size={15} />
            <span>Total Amount</span>
            <strong style={{ fontSize: 20 }}>{formatAmount(order.finalAmount)}</strong>
          </div>
        </div>
      </div>

      {/* Live Repairing Card */}
      <div className="detail-section-card" style={{ borderTop: '3px solid #ef4444' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Radio size={16} color="#ef4444" />
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Live Repairing</h2>
            {liveSession && (
              <span style={{ background: '#ef4444', color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700, animation: 'pulse 1.5s infinite' }}>
                LIVE
              </span>
            )}
          </div>
          {liveSession ? (
            <button
              onClick={handleEndLive}
              disabled={liveLoading}
              style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              {liveLoading ? 'Ending...' : 'End Live'}
            </button>
          ) : (
            <button
              onClick={handleGoLive}
              disabled={liveLoading}
              style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: liveLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Radio size={13} />
              {liveLoading ? 'Creating...' : 'Go Live'}
            </button>
          )}
        </div>

        {liveSession && (
          <div style={{ background: '#fef2f2', borderRadius: 10, padding: 12 }}>
            {/* Open YouTube Studio — primary CTA */}
            {liveSession.youTubeStudioUrl && (
              <a
                href={liveSession.youTubeStudioUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', background: '#ef4444', color: '#fff', borderRadius: 10, padding: '11px', fontSize: 15, fontWeight: 700, textDecoration: 'none', marginBottom: 12 }}
              >
                <Radio size={16} />
                Open YouTube Studio to Stream
              </a>
            )}

            {/* RTMP stream key for OBS / advanced users */}
            {liveSession.streamRtmpUrl && (
              <div style={{ background: '#fff', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>RTMP URL (for OBS / streaming apps)</div>
                <div style={{ fontSize: 12, color: '#334155', fontFamily: 'monospace', wordBreak: 'break-all' }}>{liveSession.streamRtmpUrl}</div>
              </div>
            )}
            {liveSession.streamKey && (
              <div style={{ background: '#fff', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Stream Key</div>
                <div style={{ fontSize: 12, color: '#334155', fontFamily: 'monospace', wordBreak: 'break-all' }}>{liveSession.streamKey}</div>
              </div>
            )}

            {/* Preview embed */}
            {liveSession.youtubeVideoId && (
              <div style={{ borderRadius: 10, overflow: 'hidden', aspectRatio: '16/9' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${liveSession.youtubeVideoId}?autoplay=0`}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Live Repair Stream"
                />
              </div>
            )}
          </div>
        )}

        {!liveSession && (
          <div>
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
              Stream your repair live on YouTube — broadcast is created automatically on Minto's channel. Customers can watch in real time on mintorepairs.com.
            </p>
            <button
              onClick={handleGoLive}
              disabled={liveLoading}
              style={{ width: '100%', background: liveLoading ? '#fca5a5' : '#ef4444', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 700, cursor: liveLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Radio size={16} />
              {liveLoading ? 'Creating broadcast...' : 'Go Live'}
            </button>
          </div>
        )}
      </div>

      <div className="detail-section-card order-actions-card">
        <h2>Work Progress</h2>

        {actionMessage && <div className="status-message">{actionMessage}</div>}
        {actionError && <div className="error-message">{actionError}</div>}
        {paymentConfirmed && (
          <div className="payment-success-banner">
            <CheckCircle size={16} />
            Payment confirmed by Cashfree
          </div>
        )}

        {isPending && (
          <>
            <button
              type="button"
              className="primary-action"
              disabled={actionLoading}
              onClick={() => beforePhotoRef.current?.click()}
            >
              {actionLoading ? <RefreshCw size={16} /> : <PlayCircle size={16} />}
              Start Order & Upload Before Photo
            </button>
            <input
              ref={beforePhotoRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden-file-input"
              onChange={handleBeforePhoto}
            />
          </>
        )}

        {isInProgress && (
          <>
            <button
              type="button"
              className="primary-action success"
              disabled={actionLoading}
              onClick={() => setChecklistOpen((current) => !current)}
            >
              <CheckCircle size={16} />
              Mark Completed
            </button>

            {checklistOpen && (
              <form className="checklist-card" onSubmit={handleCompleteOrder}>
                <div className="checklist-grid">
                  {checklistItems.map((item) => (
                    <label className="checklist-item" key={item.key}>
                      <input
                        type="checkbox"
                        checked={checklist[item.key]}
                        onChange={() => handleChecklistChange(item.key)}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>

                <label className="photo-input-label">
                  <Upload size={16} />
                  <span>{afterPhoto ? afterPhoto.name : 'Upload After Repair Photo'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(event) => setAfterPhoto(event.target.files?.[0] || null)}
                  />
                </label>

                <button type="submit" className="primary-action" disabled={actionLoading}>
                  {actionLoading ? <RefreshCw size={16} /> : <CheckCircle size={16} />}
                  Save Checklist & Complete
                </button>
              </form>
            )}
          </>
        )}

        {shouldShowPaymentQr && (
          <div className="payment-qr-card">
            <div className="payment-qr-header">
              <div>
                <span>Collect Payment</span>
                <strong>{formatAmount(paymentQr?.amount || order.finalAmount)}</strong>
              </div>
              <QrCode size={22} />
            </div>

            {paymentLoading && <div className="empty-state">Loading QR...</div>}
            {!paymentLoading && qrImageSrc && (
              <img className="qr-image" src={qrImageSrc} alt="Payment QR code" />
            )}

            <div className="payment-actions">
              <button
                type="button"
                className="secondary-action"
                disabled={actionLoading}
                onClick={() => handlePaymentReceived('Cash')}
              >
                <CreditCard size={15} />
                Cash Received
              </button>
              <button
                type="button"
                className="secondary-action"
                disabled={actionLoading}
                onClick={() => handlePaymentReceived('Online')}
              >
                <CreditCard size={15} />
                Online Received
              </button>
            </div>
          </div>
        )}

        {isPaymentReceived && (
          <div className="payment-success-banner">
            <CheckCircle size={16} />
            Payment Received
          </div>
        )}

        {!isPending && !isInProgress && !shouldShowPaymentQr && !isPaymentReceived && (
          <div className="empty-state compact-empty">No technician action available for this status.</div>
        )}
      </div>

      <div className="detail-section-card">
        <h2>Customer</h2>
        <div className="detail-line">
          <Phone size={15} />
          <div>
            <strong>{order.customerName}</strong>
            <span>{order.customerMobile}</span>
          </div>
        </div>
        <div className="detail-line">
          <MapPin size={15} />
          <div style={{ flex: 1 }}>
            <strong>Address</strong>
            <span>{order.customerAddress || '-'}</span>
            {order.customerAddress && (
              <div className="map-action-row">
                <a
                  className="map-btn navigate"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(order.customerAddress)}&travelmode=driving`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Navigation size={13} />
                  Navigate
                </a>
                <a
                  className="map-btn station"
                  href={`https://www.google.com/maps/search/metro+OR+railway+station+near+${encodeURIComponent(order.customerAddress)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Train size={13} />
                  Nearby Station
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="detail-section-card">
        <h2>Order Details</h2>
        <div className="detail-grid">
          <div><span>Date</span><strong>{formatDate(order.orderDate)}</strong></div>
          <div><span>Payment</span><strong>{order.paymentStatus || '-'}</strong></div>
          <div><span>Method</span><strong>{order.paymentMethod || '-'}</strong></div>
          <div><span>Total Amount</span><strong>{formatAmount(order.finalAmount)}</strong></div>
          <div><span>Technician</span><strong>{order.technicianName || '-'}</strong></div>
          <div className="wide"><span>Notes</span><strong>{order.notes || '-'}</strong></div>
        </div>
      </div>

      {!!order.orderItems?.length && (
        <div className="detail-section-card">
          <h2>Services</h2>
          <div className="service-list">
            {order.orderItems.map((item) => (
              <div className="service-row" key={item.id}>
                <div>
                  <strong>{item.serviceName}</strong>
                  <span>{item.serviceType} | Qty {item.quantity}</span>
                </div>
                <div className="service-cost">
                  <span>You Earn</span>
                  <strong>{formatAmount(item.serviceCost)}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule footer bar */}
      {order.scheduledDate && order.scheduledTime && (() => {
        const dt = parseScheduledDt(order.scheduledDate, order.scheduledTime);
        if (!dt) return null;
        const diff = dt.getTime() - now;
        const delayed = diff < 0;
        const absMins = Math.round(Math.abs(diff) / 60000);
        const h = Math.floor(absMins / 60);
        const m = absMins % 60;
        const parts: string[] = [];
        if (h > 0) parts.push(`${h}h`);
        parts.push(`${m}m`);
        const tickerText = delayed ? `Delayed by ${parts.join(' ')}` : `${parts.join(' ')} left to start`;
        const displayTime = order.scheduledTime.replace(/:00\s*(AM|PM)$/i, ' $1').replace(/:00$/, '');
        const schedDateDisplay = new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
        return (
          <div style={{
            position: 'fixed', bottom: 60, left: 0, right: 0, zIndex: 40,
            background: delayed ? '#7f1d1d' : '#1e3a5f',
            color: '#fff',
            padding: '10px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: '0 -2px 12px rgba(0,0,0,0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={15} />
              <div>
                <div style={{ fontSize: 11, opacity: 0.75 }}>Scheduled · {schedDateDisplay}</div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{displayTime}</div>
              </div>
            </div>
            <div style={{
              background: delayed ? '#ef4444' : '#22c55e',
              borderRadius: 20, padding: '4px 12px',
              fontSize: 12, fontWeight: 700,
            }}>
              {tickerText}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default OrderDetailPage;
